// @flow
import React from 'react';
import { imageURL, numberWithCommas, parseArrayString } from "lib/common";
import Button from 'components/common/Button';
import { Link } from 'react-router-dom';
import { camelCase, kebabCase, get } from 'lodash';
import './CheckoutTemplate.scss';

type Props = {
  logged: boolean,
  cart: any,
  handleChange: any,
  onCheckOut: any,
  checkoutTerms: boolean,
  checkoutPrivacy: boolean,
  checkoutAll: boolean,
  onCheckoutTerms: any,
  onCheckoutPrivacy: any,
  onCheckoutAll: any,
  onCartCoupon: any,
  applyCoupons: any,
  applyDiscounts: any,
  appliedDiscount: string,
  onPaymentMethod: any,
  addressNameFull: string,
  addressEmail: string,
  addressMobile: string,
  addressPhone: string,
  addressState: string,
  addressCity: string,
  addressAddress1: string,
  addressAddress2: string,
  addressPostCode: string,
  request: string,
  isAuthenticated: boolean,
};

const translationKeys = {
  'name.first': 'firstName',
  'name.last': 'lastName',
  'name.full': 'fullName',
};

const toFieldDetail = (prefix = '') => field => {
  const [key, required] = field.split(':');
  return {
    required: required === 'required',
    key,
    translationKey: camelCase([prefix, translationKeys[key] || key]),
  };
};

const paymentMethods = [
  // {
  //   id: 'naverpay',
  //   label: 'naverPay',
  //   value: '네이버페이',
  //   cardFields: false,
  //   payLater: false,
  // },
  {
    id: 'iamport',
    label: 'niceByCard',
    cardFields: false,
    payLater: false,
    meta: {
      pg: 'nice',
      payMethod: 'card',
    },
  },
  {
    id: 'virtual-bank',
    label: 'virtualBank',
    cardFields: false,
    payLater: false,
    meta: {
      pg: 'nice',
      payMethod: 'vbank',
    },
  },
  {
    id: 'research-transfer',
    label: 'researchTransfer',
    cardFields: false,
    payLater: true,
  },
  {
    id: 'kb-bank-transfer',
    label: 'kbBankTransfer',
    cardFields: false,
    payLater: true,
  },
  {
    id: 'bank-transfer',
    label: 'bankTransfer',
    cardFields: false,
    payLater: true,
  },
  {
    id: 'post-transfer',
    label: 'postTransfer',
    cardFields: false,
    payLater: true,
  },
  {
    id: 'quick-bank-transfer',
    label: 'quickBankTransfer',
    cardFields: false,
    payLater: true,
  },
];

const customerFields = parseArrayString([
  'name.full:required',
  'email:required',
  'mobile:required',
  'phone',
]).map(toFieldDetail('customer'));

const recipientFields = parseArrayString([
  'name.full:required',
  'email:required',
  'mobile:required',
  'phone',
]).map(toFieldDetail('address'));

const statusTrans = key => {
  let words = {
    checkoutPreparationFailed: '주문에 필요한 정보를 가져오는데 실패했습니다.',
    invalidCartItemIncluded: '주문할 수 없는 상품이 포함되어 있습니다. 구매 상품을 리뷰해 주세요.',
    invalidPostcode: '지원되지 않는 우편번호 형식입니다.',
    startsTooEarly: '정기 구독 시작 일시가 너무 이릅니다.',
    cartCouponCategory: '장바구니 쿠폰이 적용되는 상품이 하나도 없습니다.',
    cartCouponPrice: '장바구니 쿠폰을 적용할 수 없는 주문 총액입니다.',
    cartUpdateFailed: '장바구니에 변경 사항을 반영하는데 실패했습니다.',
    confirmItems: '구매 상품 확인',
    backToCart: '장바구니로 돌아가기',
    itemInfo: '상품 정보',
    itemQuantity: '수량',
    itemPrice: '가격',
    itemOption: '상품 옵션',
    shippingMethod: '배송 방식',
    appliedCoupon: '적용 쿠폰',
    selectProductCoupon: '상품 쿠폰 선택',
    selectCartCoupon: '장바구니 쿠폰 선택',
    alreadyDiscounted: '이미 할인된 경우 적용할 수 없는 쿠폰입니다.',
    mustBeOneQuantity: '담은 수량이 1개인 품목에만 적용할 수 있습니다.',
    duplicatedCoupon: '동일한 쿠폰을 여러품목에 적용할 수 없습니다.',
    applyCoupons: '쿠폰 적용',
    finishApplyingCoupons: '쿠폰 적용 완료',
    selectSubscriptionPlanFirst: '정기 주문 방식을 먼저 선택해야합니다.',
    required: '필수',
    setCustomer: '주문자 정보',
    customerFirstName: '주문자 이름',
    customerLastName: '주문자 성',
    customerFullName: '주문자명',
    customerEmail: '이메일',
    customerMobile: '핸드폰 번호',
    customerPhone: '사업자 등록 번호',
    goToUpdateCustomer: '내 기본 정보 변경',
    sameForRecipient: '수취자 정보도 위와 동일합니다.',
    customerFirstNameRequired: '주문자 이름을 입력해야합니다.',
    customerLastNameRequired: '주문자 성을 입력해야합니다.',
    customerFullNameRequired: '주문자명을 입력해야합니다.',
    customerEmailRequired: '이메일을 입력해야합니다.',
    customerMobileRequired: '핸드폰 번호를 입력해야합니다.',
    customerPhoneRequired: '사업자 등록 번호를 입력해야합니다.',
    setShippingAddress: '배송 주소',
    recipientInfo: '수취자 정보',
    addressFirstName: '수취자 이름',
    addressLastName: '수취자 성',
    addressFullName: '수취자명',
    addressEmail: '이메일',
    addressMobile: '핸드폰 번호',
    addressPhone: '사업자 등록 번호',
    addressCountry: '국가',
    addressState: '도',
    addressCity: '시',
    addressAddress1: '주소',
    addressAddress2: '상세주소',
    addressPostcode: '우편번호',
    searchAddress: '주소 찾기',
    saveAsPrimaryAddress: '이 주소를 기본 주소로 저장',
    applyCouponsFirst: '쿠폰 적용을 먼저 끝내야합니다.',
    applyAddress: '설정된 주소 적용',
    doOrder: '주문하기',
    doSubscription: '정기 주문하기',
    addressFirstNameRequired: '수취자 이름을 입력해야합니다.',
    addressLastNameRequired: '수취자 성을 입력해야합니다.',
    addressFullNameRequired: '수취자명을 입력해야합니다.',
    addressMobileRequired: '무선 연락처를 입력해야합니다.',
    addressPhoneRequired: '유선 연락처를 입력해야합니다.',
    addressCountryRequired: '배송 주소의 국가를 입력해야합니다.',
    addressStateRequired: '배송 주소의 도를 입력해야합니다.',
    addressCityRequired: '배송 주소의 시를 입력해야합니다.',
    addressAddress1Required: '배송 주소의 주소를 입력해야합니다.',
    addressAddress2Required: '배송 주소의 상세 주소를 입력해야합니다.',
    addressPostcodeRequired: '배송 주소의 우편번호를 입력해야합니다.',
    orderRequest: '요청 사항',
    typeOrderRequest: '요청 사항을 입력해주세요.',
    subscription: '정기 주문',
    subscriptionPlan: '정기 주문 방식',
    subscriptionPlanRequired: '정기 주문 방식을 선택해주세요.',
    selectSubscriptionPlan: '정기 주문 방식 선택',
    firstOrderStartsAt: '첫 주문일',
    secondOrderStartsAt: '두번째 주문일',
    subscriptionStartsAtRequired: '주문 시작일을 선택해주세요.',
    selectSubscriptionStartsAt: '주문 시작일 선택',
    firstOrderIsImmediate: '첫 주문은 즉시 주문됩니다.',
    payment: '결제',
    orderSummary: '주문 요약',
    subscriptionSummary: '정기 주문 요약',
    firstOrderSummary: '첫 주문',
    firstNowOrder: '즉시 주문',
    restOrdersSummary: '이후 주문',
    itemTotal: '상품 합계',
    shippingTotal: '배송비 합계',
    taxTotal: '부가세',
    orderTotal: '총계',
    agreeWithTerms: '이용 약관에 동의합니다.',
    viewTerms: '이용 약관 보기',
    agreeWithPrivacy: '개인정보 수집·이용에 동의합니다.',
    viewPrivacy: '개인정보 수집·이용 보기',
    agreeWithAll: '전체 동의',
    termsAgreementRequired: '이용 약관에 동의해야합니다.',
    privacyAgreementRequired: '개인정보 수집·이용에 동의해야합니다.',
    taxIncludedTip: '부가세는 가격에 포함 되어있습니다.',
    taxExcludedTip: '부가세는 가격에 미포함 되어있습니다.',
    shippingFeeAndTaxTip: '배송비와 부가세는 배송 주소 설정 후에 계산됩니다.',
    proceedOrder: '주문 및 결제하기',
    proceedSubscription: '정기 주문 및 결제하기',
    checkoutFailed: '주문에 실패했습니다.',
    checkoutProcessFailed: '주문 절차 진행에 실패했습니다.',
    paymentFailed: '결제에 실패했습니다. 결제를 완료해주세요.',
    scheduleFailed: '정기 주문 예약에 실패했습니다.',
    paymentMethod: '결제 방식',
    cardNumber: '카드 번호',
    isCompanyCard: '법인 카드',
    cardExpiration: '만료일',
    cardPassword2: '비밀번호 첫 2자리',
    cardOwnerBirthdate: '생년월일',
    cardOwnerCompanyNumber: '사업자등록번호',
    selectPaymentMethod: '결제 방식을 선택해주세요.',
    paymentMethodRequired: '결제 방식을 선택해주세요.',
    cardNumberRequired: '카드 번호를 입력해주세요.',
    cardExpirationMonthRequired: '카드 만료 월을 입력해주세요.',
    cardExpirationYearRequired: '카드 만료 년도를 입력해주세요.',
    cardPassword2Required: '카드 비밀번호 첫 2자리를 입력해주세요.',
    cardOwnerRequired: '생년월일 또는 사업자등록번호를 입력해주세요.',
    // naverPay: '네이버페이',
    niceByCard: '신용카드',
    virtualBank: '가상계좌',
    researchTransfer: '연구비카드',
    kbBankTransfer: '세금계산서 (신한은행 100-032-703829)',
    bankTransfer: '세금계산서 (기업은행 033-502993-01-019)',
  };
  return words[key];
};

const CheckoutTemplate = (props: Props) => (
  <div className="CheckoutTemplate">
    <div className="checkout order-checkout">
      <div className="checkout-process">
        <div className="cart-items">
          <h2 className="with-back-button">구매 상품 확인</h2>

          <Link className="back-to-cart" to={'/cart'}>
            장바구니로 돌아가기
          </Link>

          <table className="table">
            <thead>
              <tr>
                <th colSpan="2">상품 정보</th>
                <th>단가</th>
                <th>수량</th>
                <th>가격</th>
              </tr>
            </thead>

            <tbody>
              {props.cart.items.map((item, index) => (
                <tr key={item.id} className="cart-item" data-item={item.id}>
                  <td className="cart-item-thumbnail-container">
                    <img
                      src={imageURL(item.product.thumbnail, 120, 120)}
                      alt={item.product.name}
                      className="cart-item-thumbnail"
                    />
                  </td>

                  <td className="cart-item-name">
                    <div className="cart-item-summary">
                      <h3 className="cart-item-name">{item.product.name}</h3>
                      <ul>
                        {item.product_price && (
                          <li className="cart-item-option">
                            <span className="cart-entity-label">카탈로그 번호</span>
                            {item.product_price.part_number}
                          </li>
                        )}
                        {item.product_price && (
                          <li className="cart-item-shipping-method">
                            <span className="cart-entity-label">설명</span>
                            {item.product_price.description}
                          </li>
                        )}
                        {item.product_price && (
                          <li className="cart-item-shipping-method">
                            <span className="cart-entity-label">단위</span>
                            {item.product_price.unit}
                          </li>
                        )}
                      </ul>

                      {/* {item.applicableCoupons && item.applicableCoupons.length > 0 && (
                        <div className="cart-item-coupon-wrapper">
                          <select className="cart-item-coupon" data-item={item.id}>
                            <option value="" selected={!item.appliedCoupon}>
                              상품 쿠폰 선택
                            </option>
                            {item.applicableCoupons.map(coupon => (
                              <option
                                key={coupon._id}
                                value={coupon._id}
                                selected={
                                  !!(item.appliedCoupon && item.appliedCoupon._id === coupon._id)
                                }
                              >
                                {coupon.name} ({coupon.discount.value.converted})
                              </option>
                            ))}
                          </select>
                        </div>
                      )} */}
                    </div>
                  </td>

                  {item.price ? (
                    <td className="cart-item-unit-price">
                      <span className="cart-mobile-label">단가</span>
                      <span className="cart-item-sale-price">{numberWithCommas(item.price.sale.raw)}원</span>
                      {item.price.sale.raw !== item.price.original.raw && (
                        <span className="cart-item-compare-price">
                          {numberWithCommas(item.price.original.raw)}원
                        </span>
                      )}
                      {item.price.sale.raw !== item.price.original.raw && (
                        <span className="cart-item-discounted">(-{numberWithCommas(item.price.original.raw - item.price.sale.raw)}원)</span>
                      )}
                    </td>
                  ) : (
                    <td className="cart-item-unit-price">
                      <span className="cart-mobile-label">단가</span>
                      <span className="cart-item-sale-price">
                        {numberWithCommas(item.product_price.price)}원
                      </span>
                    </td>
                  )}

                  <td className="cart-item-quantity">
                    <span className="cart-mobile-label">수량</span>
                    <span className="cart-item-quantity-value">{item.quantity}</span>
                  </td>

                  <td className="cart-item-price">
                    <span className="cart-mobile-label">가격</span>
                    <span className="cart-item-sale-price">{numberWithCommas(item.total.price.sale.raw)}원</span>
                    {item.total.price.sale.raw !== item.total.price.original.raw && (
                      <span className="cart-item-compare-price">
                        {numberWithCommas(item.total.price.original.raw)}원
                      </span>
                    )}
                    {item.total.price.sale.raw !== item.total.price.original.raw && (
                      <span className="cart-item-discounted">(-{numberWithCommas(item.total.discounted.raw)}원)</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>

            {/* {props.logged &&
              (props.cart.appliedCoupon ||
                (props.cart.applicableCoupons && props.cart.applicableCoupons.length > 0 && (
                  <tfoot>
                    <tr>
                      <td className="cart-coupon-wrapper" colSpan="2">
                        {props.cart.appliedCoupon && (
                          <div className="cart-applied-coupon">
                            <span className="cart-entity-label">적용 쿠폰</span>
                            {props.cart.appliedCoupon.name}
                          </div>
                        )}
                        {props.cart.applicableCoupons.length > 0 && (
                          <select className="cart-coupon" onChange={props.onCartCoupon}>
                            <option value="" selected={!props.cart.appliedCoupon}>
                              장바구니 쿠폰 선택
                            </option>
                            {props.cart.applicableCoupons.map((coupon, index) => (
                              <option
                                key={coupon._id}
                                value={coupon._id}
                                selected={
                                  !!(
                                    props.cart.appliedCoupon &&
                                    props.cart.appliedCoupon._id === coupon._id
                                  )
                                }
                              >
                                {coupon.name} ({coupon.discount.value.converted})
                              </option>
                            ))}
                          </select>
                        )}
                      </td>
                      {props.cart.applicableCoupons.length > 0 && (
                        <td className="action-coupons" colSpan="2">
                          <Button className="apply-coupons" onClick={props.applyCoupons}>
                            쿠폰 적용
                          </Button>
                        </td>
                      )}
                    </tr>
                  </tfoot>
                )))} */}

            {/* {!props.logged && (
              <tfoot>
                <tr>
                  <td className="cart-coupon-wrapper" colSpan="2">
                    <div className="cart-applied-coupon">
                      <span className="cart-entity-label">할인 코드</span>
                      <input
                        type="text"
                        name="appliedDiscount"
                        value={props.appliedDiscount}
                        placeholder="할인 코드를 입력해주세요."
                        onChange={props.handleChange}
                      />
                    </div>
                  </td>
                  <td className="action-coupons" colSpan="2">
                    <div className="cart-action-coupon">
                      <Button className="apply-coupons" onClick={props.applyDiscounts}>
                        할인 코드 적용
                      </Button>
                    </div>
                  </td>
                </tr>
              </tfoot>
            )} */}
          </table>
        </div>
        <div className="recipient-info form">
          <h2>수취자 정보</h2>
          <div className="address-recipient">
            {recipientFields.map((field, index) => (
              <div key={field.key} className={`address-${kebabCase(field.key)}`}>
                <label htmlFor={field.key}>
                  {statusTrans(field.translationKey)}{' '}
                  {field.required && <span className="form-required-marker">(필수)</span>}
                  <input
                    type="text"
                    name={camelCase(`address.${field.key}`)}
                    value={props[camelCase(`address.${field.key}`)]}
                    placeholder={statusTrans(field.translationKey)}
                    required={!!field.required}
                    onChange={props.handleChange}
                  />
                </label>
              </div>
            ))}
            {props.logged && props.isAuthenticated && (
              <div className="update-customer">
                <Link className="go-to-update-customer" to={'/settings'}>
                  내 기본 정보 변경
                </Link>
              </div>
            )}
          </div>
        </div>
        <div className="address form">
          <h2>배송 주소</h2>

          <div className="action-address">
            <Button className="search-address" onClick={props.onFindAddress}>
              주소 찾기
            </Button>
          </div>

          <div className="address-location">
            <div className="address-state">
              <label htmlFor="state">
                도
                <input
                  type="text"
                  name="address.state"
                  value={props.addressState}
                  placeholder="도"
                  readOnly
                  required
                />
              </label>
            </div>

            <div className="address-city">
              <label htmlFor="city">
                시
                <input
                  type="text"
                  name="address.city"
                  value={props.addressCity}
                  placeholder="시"
                  readOnly
                  required
                />
              </label>
            </div>

            <div className="address-address1">
              <label htmlFor="address1">
                주소
                <input
                  type="text"
                  name="address.address1"
                  value={props.addressAddress1}
                  placeholder="주소"
                  readOnly
                  required
                />
              </label>
            </div>

            <div className="address-address2">
              <label htmlFor="address2">
                상세주소
                <input
                  id="addressAddress2"
                  type="text"
                  name="addressAddress2"
                  value={props.addressAddress2}
                  placeholder="상세주소"
                  onChange={props.handleChange}
                  required
                />
              </label>
            </div>

            <div className="address-postcode">
              <label htmlFor="postcode">
                우편번호
                <input
                  type="text"
                  name="address.postcode"
                  value={props.addressPostCode}
                  placeholder="우편번호"
                  readOnly
                  required
                />
              </label>
            </div>

            {/* {props.isAuthenticated && (
              <div className="save-primary-address">
                <label htmlFor="save-primary-address-agreement">
                  <input type="checkbox" checked id="save-primary-address-agreement" />
                  이 주소를 기본 주소로 저장
                </label>
              </div>
            )} */}
          </div>
        </div>

        <div className="order-payment">
          <div className="payment-details form">
            <h2>결제 방법</h2>

            <div id="payment-widget" />
            <div id="agreement" />
          </div>
        </div>

        <div className="order-request form">
          <h2>요청 사항</h2>

          <div>
            <textarea
              name="request"
              placeholder="요청 사항을 입력해주세요."
              onChange={props.handleChange}
              value={props.request}
            />
          </div>
        </div>
      </div>

      <div className="total">
        <h2>주문 요약</h2>

        <div className="total-details">
          <table>
            <tbody>
              <tr className="item-total">
                <th>상품 합계</th>
                <td>
                  <span className="cart-items-sale-price">
                    {numberWithCommas(props.cart.total.price.sale.raw)}원
                  </span>
                  {props.cart.total.price.sale.raw !==
                    props.cart.total.price.original.raw && (
                    <span className="cart-items-compare-price">
                      {numberWithCommas(props.cart.total.price.original.raw)}원
                    </span>
                  )}
                  {props.cart.total.price.sale.raw !==
                    props.cart.total.price.original.raw && (
                    <span className="cart-items-discounted">
                      (-{numberWithCommas(props.cart.total.discounted.raw)}원)
                    </span>
                  )}
                </td>
              </tr>
              <tr className="shipping-total">
                <th>배송비 합계</th>
                <td>{numberWithCommas(props.cart.total.shipping.fee.sale.raw)}원</td>
              </tr>
              <tr className="tax-total">
                <th>부가세</th>
                <td>{numberWithCommas(props.cart.total.taxed.raw)}원</td>
              </tr>
              <tr className="order-total">
                <th>총계</th>
                <td>{numberWithCommas(props.cart.total.amount.raw)}원</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="checkout-agreements">
          <div key="terms" className={`checkout-terms`} data-agreement-scope="terms">
            <label htmlFor={`checkout-terms-agreement`}>
              <input
                type="checkbox"
                required
                id={`checkout-terms-agreement`}
                checked={props.checkoutTerms}
                onChange={props.onCheckoutTerms}
              />
              {statusTrans(camelCase(`agreeWith_terms`))}
            </label>
            <a className="view-checkout-scope" href={`/policy/terms`} target="_blank">
              {statusTrans(camelCase(`view_terms`))}
            </a>
          </div>
          <div key="privacy" className={`checkout-privacy`} data-agreement-scope="privacy">
            <label htmlFor={`checkout-privacy-agreement`}>
              <input
                type="checkbox"
                required
                id={`checkout-privacy-agreement`}
                checked={props.checkoutPrivacy}
                onChange={props.onCheckoutPrivacy}
              />
              {statusTrans(camelCase(`agreeWith_privacy`))}
            </label>
            <a className="view-checkout-scope" href={`/policy/privacy`} target="_blank">
              {statusTrans(camelCase(`view_privacy`))}
            </a>
          </div>

          <div className="checkout-all" data-agreement-scope="all">
            <label htmlFor="checkout-all-agreement">
              <input
                type="checkbox"
                id="checkout-all-agreement"
                checked={props.checkoutAll}
                onChange={props.onCheckoutAll}
              />
              전체 동의
            </label>
          </div>
        </div>

        <Button className="proceed-checkout button" onClick={props.onCheckOut}>
          주문 및 결제하기
        </Button>

        <ul className="order-summary-tips">
          <li>대한민국 원 (KRW)</li>
          <li>부가세는 가격에 미포함 되어있습니다.</li>
          <li>배송비와 부가세는 배송 주소 설정 후에 계산됩니다.</li>
        </ul>
      </div>
    </div>
  </div>
);

export default CheckoutTemplate;



// WEBPACK FOOTER //
// ./src/components/checkout/CheckoutTemplate/CheckoutTemplate.js